import { ChildProcess, spawn } from "child_process";

import { ConsoleApplication } from "../../Commons/ConsoleApplication/ConsoleApplication";
import { createEvent, IEventSubscription } from "../../Commons/EventObject";

import { ProcessUtils } from "./ProcessUtils";

export interface ShellProcessStartInfo {
    command: string;
    args: string[];
    workingDirectory?: string;
}

export class ShellProcessHandle {
    private readonly startInfo: ShellProcessStartInfo;
    private process?: ChildProcess;

    public readonly onStdOut = createEvent<[string]>();
    public readonly onStdErr = createEvent<[string]>();
    private subscription?: IEventSubscription;

    public constructor(startInfo: ShellProcessStartInfo) {
        this.startInfo = startInfo;
    }

    public async kill(): Promise<void> {
        if (this.subscription != undefined) {
            this.subscription.unsubscribe();
        }
        const processToKill = this.process;
        if (processToKill == undefined) {
            throw new Error("Process not started");
        }
        const pid = processToKill.pid;
        this.process = undefined;
        processToKill.stderr?.pause();
        processToKill.stdout?.pause();
        // @ts-ignore
        await ProcessUtils.killTreeAsync(pid);
    }

    public waitOutputIncludesString(expectedString: string): Promise<void> {
        return new Promise<void>(resolve => {
            const subscription = this.onStdOut.subscribe(output => {
                if (output.includes(expectedString)) {
                    subscription.unsubscribe();
                    resolve();
                }
            });
        });
    }

    public start(): void {
        this.subscription = ConsoleApplication.onExit.subscribe(this.handleExit);
        let shellExe: string, shellArg: string[];
        if (process.platform === "win32") {
            shellExe = "cmd.exe";
            shellArg = ["/c", this.startInfo.command, ...this.startInfo.args];
        } else {
            shellExe = "/bin/sh";
            shellArg = ["-c", [this.startInfo.command, ...this.startInfo.args].join(" ")];
        }
        this.process = spawn(shellExe, shellArg, {
            cwd: this.startInfo.workingDirectory,
            stdio: "pipe",
        });
        this.process.stdout?.on("data", (data: Buffer | string) => {
            this.onStdOut.emit(data.toString());
        });
        this.process.stderr?.on("data", (data: Buffer | string) => {
            this.onStdErr.emit(data.toString());
        });
    }

    public waitForExit(): Promise<null | number> {
        const currentProcess = this.process;
        if (currentProcess != undefined) {
            return new Promise<null | number>(resolve => {
                currentProcess.on("exit", code => {
                    resolve(code);
                });
            });
        } else {
            throw new Error("No process started");
        }
    }

    private readonly handleExit = async (): Promise<void> => {
        await this.kill();
    };
}
